import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import {
  Container,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  InputAdornment,
  Chip,
  Paper,
  Pagination,
  CircularProgress,
  Alert
} from '@mui/material'
import { Search, Description } from '@mui/icons-material'

const PublicProcedimientosList = () => {
  const navigate = useNavigate()
  const { gestionId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()

  const [procedimientos, setProcedimientos] = useState([])
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total_records: 0 })
  const [gestion, setGestion] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const search = searchParams.get('search') || ''
  const page = parseInt(searchParams.get('page') || '1', 10)

  useEffect(() => {
    loadProcedimientos()
  }, [gestionId, search, page])

  useEffect(() => {
    if (gestionId) {
      loadGestion()
    } else {
      setGestion(null)
    }
  }, [gestionId])

  const loadGestion = async () => {
    try {
      const response = await axios.get('/api/publico/gestiones')
      const found = (response.data || []).find((g) => String(g.id) === String(gestionId))
      setGestion(found || null)
    } catch (err) {
      console.error('Error cargando gestión:', err)
    }
  }

  const loadProcedimientos = async () => {
    try {
      setLoading(true)
      setError(null)
      const params = { page, limit: 12 }
      if (gestionId) params.gestion_id = gestionId
      if (search) params.search = search
      const response = await axios.get('/api/publico/procedimientos', { params })
      setProcedimientos(response.data.procedimientos || [])
      setPagination(response.data.pagination || { current_page: 1, total_pages: 1, total_records: 0 })
    } catch (err) {
      console.error('Error cargando procedimientos públicos:', err)
      setError('No se pudo cargar los procedimientos.')
    } finally {
      setLoading(false)
    }
  }

  const handleSearchChange = (value) => {
    const next = new URLSearchParams(searchParams)
    if (value.trim()) {
      next.set('search', value.trim())
    } else {
      next.delete('search')
    }
    next.delete('page')
    setSearchParams(next, { replace: true })
  }

  const handlePageChange = (event, value) => {
    const next = new URLSearchParams(searchParams)
    if (value > 1) {
      next.set('page', String(value))
    } else {
      next.delete('page')
    }
    setSearchParams(next, { replace: true })
  }

  const title = gestion
    ? gestion.nombre
    : 'Todos los procedimientos'

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          {title}
        </Typography>
        {gestion && (
          <Chip
            label={gestion.codigo}
            size="small"
            sx={{ bgcolor: gestion.color || '#64B5F6', color: '#fff', fontWeight: 700 }}
          />
        )}
      </Box>

      <TextField
        fullWidth
        value={search}
        onChange={(e) => handleSearchChange(e.target.value)}
        placeholder="Buscar por título, código o proceso..."
        variant="outlined"
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          )
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper>
            <List disablePadding>
              {procedimientos.map((proc) => (
                <ListItem key={proc.id} disablePadding divider>
                  <ListItemButton onClick={() => navigate(`/publico/procedimiento/${proc.id}`)}>
                    <Description sx={{ color: proc.gestion_color || 'primary.main', mr: 2 }} />
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {proc.codigo} — {proc.titulo}
                        </Typography>
                      }
                      secondary={
                        <Box component="span" sx={{ display: 'block' }}>
                          <Box component="span" sx={{ display: 'block' }}>
                            {proc.proceso}
                          </Box>
                          <Box component="span" sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                            {proc.gestion_nombre && (
                              <Chip label={proc.gestion_nombre} size="small" variant="outlined" />
                            )}
                            <Chip label={`Versión ${proc.version}`} size="small" variant="outlined" />
                          </Box>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            {procedimientos.length === 0 && (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No se encontraron procedimientos.
                </Typography>
              </Box>
            )}
          </Paper>

          {pagination.total_pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={pagination.total_pages}
                page={pagination.current_page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {pagination.total_records} procedimiento{pagination.total_records !== 1 ? 's' : ''} en total
            </Typography>
          </Box>
        </>
      )}
    </Container>
  )
}

export default PublicProcedimientosList
